import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';
import { lazy } from 'react';
import './App.css';
import ClientSideWrapper from './utils/ClientSideWrapper';
import PublicRoutes from './_config/publicRoutes';
import PrivateRoutes from './_config/privateRoutes';
import { URLS } from './_config';
import AuthLayout from './hoc/authLayout';
import PreLoader from './components/Preloader';
import { wineAdditionRoutes } from './pages/wine-addition/WineAdditionRoutes';
import { ExternalInputProvider } from './pages/wine-addition/ExternalInputContext';
import { DirectInputProvider } from './pages/wine-addition/DirectInputContext';
import RouteWatcher from './pages/wine-addition/RouteWatcher'; // Add this import

// Lazy load all pages
const Login = lazy(() => import("./pages/login"));
const Page404 = lazy(() => import("./pages/404"));
const Registration = lazy(() => import("./pages/registration"));
const ForgotPassword = lazy(() => import("./pages/forgotPassword"));
const ResetPassword = lazy(() => import("./pages/resetPassword"));
const JobLogList = lazy(() => import("./pages/dashboard/jobLogList"));
const JobLogForm = lazy(() => import("./pages/dashboard/JobLogForm"));
const CrawlerList = lazy(() => import("./pages/crawler/crawlerList"));
const ShowCrawler = lazy(() => import("./pages/crawler/showCrawler"));
const RetailerGroupList = lazy(
  () => import("./pages/crawler/retailerGroupList")
);
const WorkGroupList = lazy(() => import("./pages/work-group/workGroupList"));
const RPRollupList = lazy(() => import("./pages/rollup/rpRollup"));
const WARollupList = lazy(() => import("./pages/rollup/waRollup"));
const DownloadList = lazy(() => import("./pages/download/download"));
const UploadList = lazy(() => import("./pages/download/upload"));
const MatchedList = lazy(() => import("./pages/matched/matchedList"));
const ShowMatched = lazy(() => import("./pages/matched/showMatched"));
const NoiseParser = lazy(() => import("./pages/noise/noise-parser"));
const GlobalNoiseList = lazy(() => import("./pages/noise/globalNoiseList"));
const WineNoiseList = lazy(() => import("./pages/noise/wine-noise"));
const ProducerNoiseList = lazy(() => import("./pages/noise/producer-noise"));
const Validation = lazy(() => import("./pages/validation/validation"));
const Narrow = lazy(() => import("./pages/narrow/narrow"));
const NewCrawler = lazy(() => import("./pages/crawler/newCrawler"));
const NewRetailerGroup = lazy(() => import("./pages/crawler/newRetailerGroup"));
const MatchConfig = lazy(() => import("./pages/wine-match/match-config"));
const WineMatchConfig = lazy(
  () => import("./pages/wine-match/WineMatchConfigForm")
);
const HistoryMatch = lazy(() => import("./pages/wine-match/history-match"));
const KeywordMatch = lazy(() => import("./pages/wine-match/keyword-match"));
const ShowJobLog = lazy(() => import("./pages/dashboard/JobLogForm"));
const ShowRetailerGroup = lazy(
  () => import("./pages/crawler/showRetailerGroup")
);
const KeywordGenerator = lazy(
  () => import("./pages/wine-addition/keywordGenerator")
);
const DirectInput = lazy(() => import("./pages/wine-addition/directInput"));
const ExternalInput = lazy(() => import("./pages/wine-addition/externalInput"));
const NewGlobalNoise = lazy(() => import("./pages/noise/newGlobalNoise"));
const ShowGlobalNoise = lazy(() => import("./pages/noise/showGlobalNoise"));
const CountryList = lazy(
  () => import("./pages/site-settings/country/countryList")
);
const RegionList = lazy(
  () => import("./pages/site-settings/region/regionList")
);
const SubRegionList = lazy(
  () => import("./pages/site-settings/sub-region/subRegionList")
);
const AppellationList = lazy(
  () => import("./pages/site-settings/appellation/AppellationList")
);
const SubAppellationList = lazy(
  () => import("./pages/site-settings/sub-appellation/SubAppellationList")
);
// const LocationList = lazy(() => import('./pages/site-settings/location/locationList'));
// const LocalesList = lazy(() => import('./pages/site-settings/locales/localesList'));
const VarietiesList = lazy(
  () => import("./pages/site-settings/varieties/VarietiesList")
);
const WineAppellationsList = lazy(
  () => import("./pages/wine-appellations/wineAppellationsList")
);
const WineMatchConfigForm = lazy(
  () => import("./pages/wine-match/WineMatchConfigForm")
);
const NewProducer = lazy(() => import("./pages/wine-addition/newProducer"));
const NewProducerNoise = lazy(() => import("./pages/noise/newProducerNoise"));
const ShowProducerNoise = lazy(() => import("./pages/noise/showProducerNoise"));
function App() {
  return (
    <PreLoader>
      <ClientSideWrapper>
        <ExternalInputProvider>
          <Router basename={process.env.PUBLIC_URL}>
            <RouteWatcher /> {/* Add this component inside Router */}
            <Suspense
              // fallback={
              //   <div className="flex items-center justify-center h-screen">
              //     <div className="text-2xl text-gray-500">Loading...</div>
              //   </div>
              // }
            >
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route element={<PublicRoutes />}>
                    <Route path="/" element={<Login />} />
                    <Route path={URLS.AUTH.SIGNIN} element={<Login />}></Route>
                    <Route
                      path={URLS.AUTH.SIGNUP}
                      element={<Registration />}
                    ></Route>
                    <Route
                      path={URLS.AUTH.FORGOT_PASSWORD}
                      element={<ForgotPassword />}
                    ></Route>
                    <Route
                      path={URLS.AUTH.RESET_PASSWORD}
                      element={<ResetPassword />}
                    ></Route>
                  </Route>

                  {/* Private routes */}
                  <Route element={<PrivateRoutes />}>
                    <Route element={<AuthLayout />}>
                      <Route
                        path={URLS.JOB_LOG.LIST}
                        element={<JobLogList />}
                      />
                      <Route
                        path={URLS.JOB_LOG.CREATE}
                        element={<JobLogForm />}
                      />
                      <Route
                        path={URLS.JOB_LOG.DETAIL}
                        element={<JobLogForm />}
                      />
                      <Route
                        path={URLS.CRAWLER.LIST}
                        element={<CrawlerList />}
                      />
                      <Route
                        path={URLS.CRAWLER.CREATE}
                        element={<NewCrawler />}
                      />
                      <Route
                        path={URLS.CRAWLER.DETAIL}
                        element={<ShowCrawler />}
                      />
                      <Route
                        path={URLS.CRAWLER.EDIT}
                        element={<NewCrawler />}
                      />
                      <Route
                        path={URLS.CRAWLER.NEW_CRAWLER}
                        element={<NewCrawler />}
                      />
                      <Route
                        path={URLS.CRAWLER.RETAILER_GROUP.CREATE}
                        element={<NewRetailerGroup />}
                      />
                      <Route
                        path={URLS.CRAWLER.RETAILER_GROUP.LIST}
                        element={<RetailerGroupList />}
                      />
                      <Route
                        path={URLS.CRAWLER.RETAILER_GROUP.DETAIL}
                        element={<ShowRetailerGroup />}
                      />
                      <Route
                        path={URLS.WORK_GROUP.LIST}
                        element={<WorkGroupList />}
                      />
                      <Route
                        path={URLS.ROLLUP.RP.LIST}
                        element={<RPRollupList />}
                      />
                      <Route
                        path={URLS.ROLLUP.WA.LIST}
                        element={<WARollupList />}
                      />
                      <Route
                        path={URLS.DOWNLOAD.LIST}
                        element={<DownloadList />}
                      />
                      <Route
                        path={URLS.DOWNLOAD.UPLOAD}
                        element={<UploadList />}
                      />
                      <Route
                        path={URLS.MATCHED.LIST}
                        element={<MatchedList />}
                      />
                      <Route
                        path={URLS.MATCHED.DETAIL}
                        element={<ShowMatched />}
                      />
                      <Route
                        path={URLS.NOISE.GLOBAL.LIST}
                        element={<GlobalNoiseList />}
                      />

                      <Route
                        path={URLS.NOISE.NOISE_PARSER.LIST}
                        element={<NoiseParser />}
                      />
                      <Route
                        path={URLS.NOISE.WINE_NOISE.LIST}
                        element={<WineNoiseList />}
                      />

                      <Route
                        path={URLS.NOISE.PRODUCER_NOISE.LIST}
                        element={<ProducerNoiseList />}
                      />
                      <Route
                        path={URLS.WINE_MATCH.MATCH_CONFIG.LIST}
                        element={<MatchConfig />}
                      />
                      <Route
                        path={URLS.WINE_MATCH.MATCH_CONFIG.CREATE}
                        element={<WineMatchConfig />}
                      />
                      <Route
                        path={URLS.WINE_MATCH.MATCH_CONFIG.EDIT}
                        element={<WineMatchConfigForm />}
                      />
                      <Route
                        path={URLS.WINE_MATCH.HISTORY.LIST}
                        element={<HistoryMatch />}
                      />
                      <Route
                        path={URLS.WINE_MATCH.KEYWORD.LIST}
                        element={<KeywordMatch />}
                      />
                      <Route
                        path={URLS.VALIDATION.GET}
                        element={<Validation />}
                      />
                      <Route path={URLS.NARROW.GET} element={<Narrow />} />
                      {/* <Route path={URLS.WINE_ADDITION.DIRECT_INPUT.CREATE} element={<DirectInput />} /> */}
                      {/* <Route path={URLS.WINE_ADDITION.NEW_PRODUCER} element={<NewProducer />} /> */}
                      {/* <Route path={URLS.WINE_ADDITION.EDIT_WINE} element={<NewProducer />} /> */}
                      <Route
                        path={URLS.WINE_ADDITION.KEYWORD_GENERATOR.CREATE}
                        element={<KeywordGenerator />}
                      />
                      {/* External Input now moved to wineAdditionRoutes with DirectInputProvider */}
                      <Route
                        path={URLS.LOCATION_SETTINGS.COUNTRY.LIST}
                        element={<CountryList />}
                      />
                      <Route
                        path={URLS.LOCATION_SETTINGS.REGION.LIST}
                        element={<RegionList />}
                      />
                      <Route
                        path={URLS.LOCATION_SETTINGS.SUB_REGION.LIST}
                        element={<SubRegionList />}
                      />
                      <Route
                        path={URLS.LOCATION_SETTINGS.APPELLATION.LIST}
                        element={<AppellationList />}
                      />
                      <Route
                        path={URLS.LOCATION_SETTINGS.SUB_APPELLATION.LIST}
                        element={<SubAppellationList />}
                      />
                      {/* <Route path={URLS.LOCATION_SETTINGS.LOCATION.LIST} element={<LocationList />} />
                                            <Route path={URLS.LOCATION_SETTINGS.LOCALES.LIST} element={<LocalesList />} /> */}
                      <Route
                        path={URLS.WINE_APPELLATIONS.LIST}
                        element={<WineAppellationsList />}
                      />
                      <Route
                        path={URLS.LOCATION_SETTINGS.VARIETIES.LIST}
                        element={<VarietiesList />}
                      />
                      {/* <Route path={URLS.WINE_ADDITION.ADD_WINE_TO_PRODUCER} element={<NewProducer />} /> */}
                      {/* Spread wineAdditionRoutes here */}
                      {wineAdditionRoutes}
                    </Route>
                  </Route>

                  <Route path={`*`} element={<Page404 />}></Route>
                </Routes>
              </div>
            </Suspense>
          </Router>
        </ExternalInputProvider>
      </ClientSideWrapper>
    </PreLoader>
  );
}

export default App;
